import React, { useEffect, useState, useRef, useLayoutEffect } from 'react'
import queryString from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import yn from 'yn'

import appRoutes from '../../configuration/appRoutes'
import { ENABLE_MONERIS_CHECKOUT } from '../../configuration/constants'
import {
  fetchTelephoneBooking,
  checkoutResetAction
} from '../../redux/telephoneBooking/actions'
import { enableCanUseApp } from '../../redux/configuration/actions'
import { setPaymentFerryId } from '../../redux/paymentConfirmation/actions'
import { preloadTransaction, clearReceipts } from '../../redux/booking/actions'

import { selectLabels } from '../../redux/configuration/selectors'
import { selectLanguage, selectSessionId } from '../../redux/session/selectors'
import { selectPaymentFerryId } from '../../redux/paymentConfirmation/selectors'
import { selectTelephoneBookingState } from '../../redux/telephoneBooking/selectors'

import BrandLoader from '../../components/BrandLoader/BrandLoader'
import MonerisRedirectForm from '../../components/BookingInformation/MonerisRedirectForm'
import MonerisCheckoutContainer from '../../components/MonerisCheckout/MonerisCheckoutContainer'
import supportedCurrencies from '../../configuration/supportedCurrencies'
import PaymentErrorModal from '../../components/PaymentErrorModal'
import TransactionNotCompleted from '../../components/TransactionNotCompleted/TransactionNotCompleted'

import { preparePreloadData } from '../../components/MonerisCheckout/utilities'

const TelephoneBookingContainer = ({
  handleRouteChange,
  openModal,
  closeModal,
  modalIsOpen
}) => {
  const params = queryString.parse(window.location.search)
  const telephoneBookingId = params?.data

  const dispatch = useDispatch()
  const telephoneBookingState = useSelector(selectTelephoneBookingState)
  const lang = useSelector(selectLanguage)
  const labels = useSelector(selectLabels)
  const sessionId = useSelector(selectSessionId)
  const paymentFerryIdDefault = useSelector(selectPaymentFerryId)
  const [hasFetchedBookingData, setHasFetchedBookingData] = useState(false)
  const [monerisCheckoutWidth, setMonerisCheckoutWidth] = useState(null)
  const paymentContainerRef = useRef(null)
  const paymentFerryId =
    telephoneBookingState?.data?.ferryId || paymentFerryIdDefault

  useEffect(() => {
    // we need to override any app disabling on this view
    dispatch(enableCanUseApp())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const containerWidth = paymentContainerRef?.current?.getBoundingClientRect()
      ?.width

    if (containerWidth && !monerisCheckoutWidth) {
      setMonerisCheckoutWidth(containerWidth >= 672 ? 672 : containerWidth - 2)
    }
  }, [monerisCheckoutWidth])

  useEffect(() => {
    if (
      telephoneBookingId &&
      !telephoneBookingState?.loading &&
      !telephoneBookingState?.error &&
      !telephoneBookingState.data &&
      !hasFetchedBookingData
    ) {
      setHasFetchedBookingData(true)
      // if we use multiple ferryIds we don't pass anything to the api and it will derive it from the booking
      const ferryId = yn(process.env.REACT_APP_REQUIRES_MULTIPLE_FERRY_IDS)
        ? null
        : process.env.REACT_APP_FLOW_CLIENT_ID
      dispatch(
        fetchTelephoneBooking(telephoneBookingId, ferryId, sessionId)
      ).then(res => {
        dispatch(setPaymentFerryId(res?.payload?.ferryId))
      })
    }
  }, [
    telephoneBookingId,
    hasFetchedBookingData,
    setHasFetchedBookingData,
    sessionId,
    dispatch,
    telephoneBookingState
  ])
  // Our API and moneris take different codes.
  const currencyCodes = supportedCurrencies.find(
    item =>
      item.monerisCode ===
      telephoneBookingState?.data?.monerisData?.currencyCode
  )

  const continueCheckoutHandler = () => {
    closeModal({ type: 'payment-error' })
    dispatch(clearReceipts()) // remove receipt error
    dispatch(checkoutResetAction)
    const bookingConfirmationData = telephoneBookingState.data
    const pageData = preparePreloadData({
      ferryId: paymentFerryId,
      bookingConfirmationData,
      lang,
      labels,
      sessionId,
      isTelephoneBooking: true
    })
    if (!pageData) return console.warn('>>> preparePreloadData failed!')
    // fetching ticket
    dispatch(
      preloadTransaction(
        { ...pageData.preloadParams },
        bookingConfirmationData.bookingNumber
      )
    )
  }

  // TODO: redirect to home page...
  const cancelCheckoutHandler = () => {
    closeModal({ type: 'payment-error' })
    dispatch(clearReceipts())
    handleRouteChange(appRoutes.account.pathname)
  }
  // TODO: ...???
  const closeCheckoutHandler = () => {
    closeModal({ type: 'payment-error' })
    dispatch(clearReceipts())
  }

  // the moneris form is hidden so we'll just show the loader unless we have an error
  return (
    <div id="telephone-booking-ctnr">
      {Boolean(telephoneBookingState?.error) ? (
        <p className="u-text-center" style={{ margin: '32px 0' }}>
          {telephoneBookingState?.error}
        </p>
      ) : (
        telephoneBookingState.loading && <BrandLoader />
      )}

      {telephoneBookingState?.data &&
        (ENABLE_MONERIS_CHECKOUT ? (
          <MonerisCheckoutContainer
            bookingConfirmationData={telephoneBookingState.data}
            ferryId={paymentFerryId}
            labels={labels}
            lang={lang}
            sessionId={sessionId}
            handleRouteChange={handleRouteChange}
            openModal={openModal}
            modalIsOpen={modalIsOpen}
            cancelTransactionRouteTarget={'payment-error'}
            monerisCheckoutWidth={monerisCheckoutWidth}
            paymentContainerRef={paymentContainerRef}
            isTelephoneBooking={true}
          />
        ) : (
          <MonerisRedirectForm
            labels={labels}
            lang={lang}
            bookingData={telephoneBookingState.data}
            currencyType={currencyCodes ? currencyCodes.APICode : 'CAD'}
            isTelephoneBooking={true}
          />
        ))}
      {/* handle payment error actions */}
      <PaymentErrorModal
        isOpen={Object.keys(modalIsOpen).includes('payment-error')}
        continueLabel={labels.retryPayment}
        cancelLabel={labels.cancelBooking}
        labels={{
          transactionCancelled: 'Transaction cancelled',
          genericPaymentErrorMessage: 'Payment refused!'
        }}
        onCloseButtonClick={closeCheckoutHandler}
        onCancel={cancelCheckoutHandler}
        onContinue={continueCheckoutHandler}
      >
        <TransactionNotCompleted language={lang} />
      </PaymentErrorModal>
    </div>
  )
}

export default TelephoneBookingContainer
